import { IVector3 } from './IVector3.js';
export class IRay {
    constructor(origin, direction, length) {
        return {
            origin: origin,
            direction: direction,
            length: length !== null && length !== void 0 ? length : Number.MAX_VALUE
        };
    }
    /**
     * Checks if ray intersects aabb and returns the distance from ray origin to hit surface point of aabb
     * negative return value indicates a miss
     * @param ray to intersect with
     * @param bounding_box to intersect with
     * @param out_intersection_distances optional output variable to store intersection distances (near and far intersection)
     * @returns true if the infinite line along the ray intersects the bounding box
     */
    static intersects_bounding_box(ray, bounding_box, out_intersection_distances) {
        let tmin = Number.NEGATIVE_INFINITY;
        let tmax = Number.POSITIVE_INFINITY;
        if (ray.direction.x !== 0.0) {
            const t1 = (bounding_box.min.x - ray.origin.x) / ray.direction.x;
            const t2 = (bounding_box.max.x - ray.origin.x) / ray.direction.x;
            tmin = Math.max(tmin, Math.min(t1, t2));
            tmax = Math.min(tmax, Math.max(t1, t2));
        }
        if (ray.direction.y !== 0.0) {
            const t1 = (bounding_box.min.y - ray.origin.y) / ray.direction.y;
            const t2 = (bounding_box.max.y - ray.origin.y) / ray.direction.y;
            tmin = Math.max(tmin, Math.min(t1, t2));
            tmax = Math.min(tmax, Math.max(t1, t2));
        }
        if (ray.direction.z !== 0.0) {
            const t1 = (bounding_box.min.z - ray.origin.z) / ray.direction.z;
            const t2 = (bounding_box.max.z - ray.origin.z) / ray.direction.z;
            tmin = Math.max(tmin, Math.min(t1, t2));
            tmax = Math.min(tmax, Math.max(t1, t2));
        }
        if (tmin > tmax)
            return false;
        if (out_intersection_distances !== undefined) {
            out_intersection_distances.x = tmin;
            out_intersection_distances.y = tmax;
        }
        return true;
    }
    /**
     * Checks if ray intersects sphere and returns the distance from ray origin to hit surface point of sphere
     * negative return value indicates a miss
     * @param ray to intersect with
     * @param sphere to intersect with
     * @param out_intersection_distances optional output variable to store intersection distances (near and far intersection)
     * @returns >= 0 there is a hit and the value indicates the distance to the hit surface point of the sphere, negative value is miss
     */
    static intersects_sphere(ray, sphere, out_intersection_distances) {
        // https://iquilezles.org/articles/intersectors/
        const oc = IVector3.subtract(ray.origin, sphere.position);
        const b = IVector3.dot(oc, ray.direction);
        const c = IVector3.dot(oc, oc) - Math.pow(sphere.radius, 2);
        let h = b * b - c;
        if (h < 0)
            return false;
        h = Math.sqrt(h);
        if (out_intersection_distances !== undefined) {
            out_intersection_distances.x = -b - h;
            out_intersection_distances.y = -b + h;
        }
        return true;
    }
    /**
     * Calculates the point where the given ray and plane intersect
     * @returns The intersection point, regardless if it's "behind" the ray
     */
    static get_plane_intersection(ray, plane) {
        const inverse_dot = 1.0 / IVector3.dot(plane.normal, ray.direction);
        const intersection_distance = IVector3.dot(IVector3.subtract(IVector3.scale(plane.normal, plane.d), ray.origin), plane.normal) * (Number.isFinite(inverse_dot) ? inverse_dot : 0.0);
        const intersection_point = IVector3.add(ray.origin, IVector3.scale(ray.direction, intersection_distance));
        return intersection_point;
    }
}
//# sourceMappingURL=IRay.js.map